import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Loader2, Send, Eye, MousePointerClick } from 'lucide-react';

// pdf.js is loaded on demand: its prebuilt bundle embeds its own webpack runtime,
// which breaks the main bundle (blank page) when imported statically.
let pdfjsPromise;
const loadPdfjs = () => {
  pdfjsPromise ??= import('pdfjs-dist/legacy/build/pdf.mjs').then((pdfjsLib) => {
    pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
    return pdfjsLib;
  });
  return pdfjsPromise;
};

export const LETTER_FIELDS = [
  { key: 'name', label: 'Learner full name', sample: 'Sample Learner', color: '#163329' },
  { key: 'reference', label: 'ID number', sample: '0000000000000', color: '#1565C0' },
  { key: 'teacher', label: 'Teacher name', sample: 'Teacher Name', color: '#6A1B9A' },
  { key: 'date', label: 'Date', sample: 'January 01, 2026', color: '#B45309' },
  { key: 'signature', label: 'Teacher signature', sample: 'Signature', color: '#D86B47' },
];

const PdfPage = ({ pdf, pageIndex, positions, onPick }) => {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    let task;
    (async () => {
      const page = await pdf.getPage(pageIndex + 1);
      const base = page.getViewport({ scale: 1 });
      const width = wrapRef.current?.clientWidth || 600;
      const viewport = page.getViewport({ scale: (width / base.width) * (window.devicePixelRatio || 1) });
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      task = page.render({ canvasContext: canvas.getContext('2d'), viewport });
      await task.promise.catch(() => {});
    })();
    return () => task?.cancel();
  }, [pdf, pageIndex]);

  const handleClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    onPick(pageIndex, (e.clientX - rect.left) / rect.width, (e.clientY - rect.top) / rect.height);
  };

  return (
    <div ref={wrapRef} className="relative w-full border shadow-sm cursor-crosshair bg-white" style={{ borderColor: '#E3E1DB' }} onClick={handleClick} data-testid={`letter-pdf-page-${pageIndex}`}>
      <canvas ref={canvasRef} className="w-full h-auto block" />
      {LETTER_FIELDS.filter((f) => positions[f.key]?.page === pageIndex).map((f) => {
        const pos = positions[f.key];
        const isSig = f.key === 'signature';
        return (
          <div
            key={f.key}
            className="absolute pointer-events-none text-[10px] sm:text-xs font-medium px-1.5 py-0.5 rounded whitespace-nowrap"
            style={{
              left: `${pos.x * 100}%`, top: `${pos.y * 100}%`,
              transform: isSig ? 'translate(-50%, -50%)' : 'translateY(-50%)',
              border: `1.5px dashed ${f.color}`, color: f.color, backgroundColor: 'rgba(255,255,255,0.85)',
            }}
            data-testid={`letter-marker-${f.key}`}
          >
            {f.sample}
          </div>
        );
      })}
      <span className="absolute top-1 right-1 text-[10px] px-1.5 rounded bg-black/50 text-white">Page {pageIndex + 1}</span>
    </div>
  );
};

export const PdfPositionEditor = ({ courseId, letter, onSaved }) => {
  const [pdf, setPdf] = useState(null);
  const [positions, setPositions] = useState(letter.positions || {});
  const [activeField, setActiveField] = useState('name');
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    let doc;
    api.get(`/courses/${courseId}/model-letter/template`, { responseType: 'arraybuffer' })
      .then(async (res) => (await loadPdfjs()).getDocument({ data: res.data }).promise)
      .then((d) => { doc = d; setPdf(d); })
      .catch(() => toast.error('Could not load your PDF'));
    return () => doc?.destroy();
  }, [courseId, letter.template_file_key]);

  useEffect(() => { setPositions(letter.positions || {}); }, [letter.positions]);

  const pick = (page, x, y) => {
    setPositions((prev) => ({ ...prev, [activeField]: { page, x: +x.toFixed(4), y: +y.toFixed(4) } }));
    const next = LETTER_FIELDS.find((f) => !positions[f.key] && f.key !== activeField);
    if (next) setActiveField(next.key);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/courses/${courseId}/model-letter/positions`, { positions });
      toast.success('Positions saved and letter sent for admin approval');
      onSaved(res.data.letter);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save positions');
    } finally {
      setSaving(false);
    }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      const res = await api.get(`/courses/${courseId}/model-letter/preview`, { responseType: 'blob' });
      window.open(URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })), '_blank');
    } catch {
      toast.error('Save the positions first, then preview');
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="pdf-position-editor">
      <div className="flex items-start gap-2 text-sm" style={{ color: '#5C635A' }}>
        <MousePointerClick className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <p>Choose a field below, then click on your letter where it should appear. Text starts at the point you click; the signature is centred on it.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {LETTER_FIELDS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setActiveField(f.key)}
            aria-pressed={activeField === f.key}
            className="text-xs px-3 py-1.5 rounded-full border transition-colors"
            style={activeField === f.key
              ? { backgroundColor: f.color, borderColor: f.color, color: '#FFFFFF' }
              : { borderColor: f.color, color: f.color }}
            data-testid={`letter-field-${f.key}`}
          >
            {positions[f.key] ? '✓ ' : ''}{f.label}{f.key === 'name' ? ' *' : ''}
          </button>
        ))}
      </div>
      <div className="space-y-4 max-h-[70vh] overflow-y-auto p-2 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
        {!pdf ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: '#163329' }} /></div>
        ) : (
          Array.from({ length: pdf.numPages }, (_, i) => <PdfPage key={i} pdf={pdf} pageIndex={i} positions={positions} onPick={pick} />)
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={save} disabled={saving || !positions.name} style={{ backgroundColor: '#163329' }} data-testid="save-letter-positions-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
          Save Positions & Submit for Approval
        </Button>
        <Button variant="outline" onClick={preview} disabled={previewing || letter.status === 'draft'} data-testid="preview-letter-btn">
          {previewing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
          Preview with sample details
        </Button>
      </div>
    </div>
  );
};
